import type { UUID } from '@aline/common';
import { format, isSameDay, isSameYear, subDays } from 'date-fns';
import { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails } from 'src/helpers';
import { appColors } from 'src/theme';

import type { ActivityComment, ActivityItemFragment } from './activity-types';
import { getIsCommentActivity } from './activity-types';

export type ChannelEmail = Apollo.ChannelEmailFieldsFragment;
export type ChannelEmailAttachment = ChannelEmail['attachments'][number];

type ThreadComment = ActivityComment | ActivityComment['replies'][number];

export interface EmailThreadMessage {
  attachments: ChannelEmailAttachment[];
  ccAddresses: string[];
  commentId: UUID;
  createdAt: string;
  /** Null for a reply posted in Aline before the email ledger recorded it. */
  fromAddress: string | null;
  /** Written by Aline AI. A person's in-app reply shares Aline's From address, so this is the signal. */
  isAiAuthored: boolean;
  senderName: string;
  text: string;
  toAddresses: string[];
}

export interface EmailThread {
  activityItemId: UUID;
  /** Aline's own addresses on this thread, lowercased: the assistant sender and the org inbox. */
  assistantAddresses: string[];
  /** Oldest first. The anchor email always comes first: every reply follows it. */
  messages: [EmailThreadMessage, ...EmailThreadMessage[]];
  subject: string;
}

export interface EmailEntrySummary {
  attachmentCount: number;
  /** "Joshua Wallace → Petter Andersson", or just the sender when no recipient was recorded. */
  context: string;
  createdAt: string;
  preview: string;
  title: string;
}

export const ACTIVITY_FILTERS = ['all', 'emails', 'comments', 'slack', 'system'] as const;
export type ActivityFilter = (typeof ACTIVITY_FILTERS)[number];

export const ACTIVITY_FILTER_LABELS = {
  all: 'All',
  comments: 'Comments',
  emails: 'Emails',
  slack: 'Slack',
  system: 'System',
} as const satisfies Record<ActivityFilter, string>;

export const ACTIVITY_FEED_FILTER = {
  all: Apollo.ActivityFeedFilter.All,
  comments: Apollo.ActivityFeedFilter.Comments,
  emails: Apollo.ActivityFeedFilter.Emails,
  slack: Apollo.ActivityFeedFilter.Slack,
  system: Apollo.ActivityFeedFilter.System,
} as const satisfies Record<ActivityFilter, Apollo.ActivityFeedFilter>;

export const ACTIVITY_FILTER_BY_FEED = {
  [Apollo.ActivityFeedFilter.All]: 'all',
  [Apollo.ActivityFeedFilter.Comments]: 'comments',
  [Apollo.ActivityFeedFilter.Emails]: 'emails',
  [Apollo.ActivityFeedFilter.Slack]: 'slack',
  [Apollo.ActivityFeedFilter.System]: 'system',
} as const satisfies Record<Apollo.ActivityFeedFilter, ActivityFilter>;

const ALINE_AI_NAME = 'Aline AI';

const PARTICIPANT_COLORS = [
  appColors.avatarLime,
  appColors.avatarOrange,
  appColors.avatarPurple,
  appColors.avatarTeal,
  appColors.primary,
];

/** The email thread an activity row anchors, or null when the row is not an email. */
export function getEmailThread(item: ActivityItemFragment): EmailThread | null {
  if (item.sourceType !== Apollo.ActivitySourceType.Email) return null;
  if (!getIsCommentActivity(item.body)) return null;
  const { comment } = item.body;
  const email = item.channelThread?.email;
  if (!comment || comment.parent || !email) return null;

  const replies = comment.replies
    .map(reply => toThreadMessage(reply, reply.email))
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  return {
    activityItemId: item.id,
    assistantAddresses: email.assistantAddresses.map(address => address.toLowerCase()),
    messages: [toThreadMessage(comment, email), ...replies],
    subject: email.subject,
  };
}

export function getEmailEntrySummary(thread: EmailThread): EmailEntrySummary {
  const latest = getLatestMessage(thread);
  const recipient = latest.toAddresses.at(0);
  const recipientName = recipient ? getAddressDisplayName(thread, recipient) : null;

  return {
    attachmentCount: thread.messages.reduce((sum, message) => sum + message.attachments.length, 0),
    context: recipientName ? `${latest.senderName} → ${recipientName}` : latest.senderName,
    createdAt: latest.createdAt,
    preview: getMessagePreview(latest.text),
    title: `Email · ${thread.subject}`,
  };
}

export function getLatestMessage(thread: EmailThread): EmailThreadMessage {
  return thread.messages.at(-1) ?? thread.messages[0];
}

/** Sender names in the order they first wrote, for the "7 messages · A, B" header. */
export function getThreadParticipantNames(thread: EmailThread): string[] {
  return [...new Set(thread.messages.map(message => message.senderName))];
}

/**
 * A name for an address on the thread. Aline's own addresses read "Aline AI"; anyone else gets
 * the display name they sent with, else the address.
 */
export function getAddressDisplayName(thread: EmailThread, address: string): string {
  if (getIsAssistantAddress(thread, address)) return ALINE_AI_NAME;
  const key = address.toLowerCase();
  const sender = thread.messages.find(message => message.fromAddress?.toLowerCase() === key);
  return sender?.senderName ?? address;
}

export function getIsAssistantAddress(thread: EmailThread, address: string): boolean {
  return thread.assistantAddresses.includes(address.toLowerCase());
}

export function getMatchesActivityFilter(
  item: Pick<ActivityItemFragment, 'itemType' | 'sourceType'>,
  filter: ActivityFilter
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'emails':
      return item.sourceType === Apollo.ActivitySourceType.Email;
    case 'slack':
      return item.sourceType === Apollo.ActivitySourceType.Slack;
    case 'system':
      return item.itemType === Apollo.ActivityItemType.StatusUpdate;
    case 'comments':
      return (
        item.itemType === Apollo.ActivityItemType.Comment &&
        item.sourceType !== Apollo.ActivitySourceType.Email &&
        item.sourceType !== Apollo.ActivitySourceType.Slack
      );
  }
}

/** "Today", "Yesterday", "Oct 19", or "Oct 19, 2025" outside the current year. */
export function getActivityDayLabel(date: Date, now: Date = new Date()): string {
  if (isSameDay(date, now)) return 'Today';
  if (isSameDay(date, subDays(now, 1))) return 'Yesterday';
  return isSameYear(date, now) ? format(date, 'MMM d') : format(date, 'MMM d, yyyy');
}

export function getIsSameActivityDay(a: string, b: string): boolean {
  return isSameDay(new Date(a), new Date(b));
}

export function formatEntryTime(createdAt: string): string {
  return format(new Date(createdAt), 'h:mm a');
}

/** "Oct 20, 2026 at 12:36 PM", for an open message. */
export function formatMessageTimestamp(createdAt: string): string {
  return format(new Date(createdAt), "MMM d, yyyy 'at' h:mm a");
}

/** "Oct 16, 9:12 AM", for a collapsed message row. */
export function formatCollapsedMessageTimestamp(createdAt: string): string {
  return format(new Date(createdAt), 'MMM d, h:mm a');
}

export function getParticipantInitials(name: string): string {
  const words = name
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  const first = words.at(0)?.charAt(0) ?? '';
  const last = words.length > 1 ? (words.at(-1)?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

/** A stable color per participant, so the same person keeps one color across messages. */
export function getParticipantColor(key: string): string {
  let hash = 0;
  for (const char of key.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return PARTICIPANT_COLORS[hash % PARTICIPANT_COLORS.length] ?? appColors.primary;
}

/** "PDF", "DOC", from the stored content type when known, else the file extension. */
export function getAttachmentTypeLabel(attachment: ChannelEmailAttachment): string {
  const extension = attachment.name.includes('.') ? attachment.name.split('.').at(-1) : null;
  if (extension) return extension.toUpperCase();
  const subtype = attachment.mimetype?.split('/').at(1);
  return subtype ? subtype.toUpperCase() : 'FILE';
}

/** "PDF · 771 KB", or just the type when the size is unknown. */
export function getAttachmentMeta(attachment: ChannelEmailAttachment): string {
  const type = getAttachmentTypeLabel(attachment);
  if (attachment.size === null) return type;
  return `${type} · ${formatAttachmentSize(attachment.size)}`;
}

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The first line of a message with markdown marks removed, for a one-line preview. */
export function getMessagePreview(text: string): string {
  const line = text
    .split('\n')
    .map(part => part.trim())
    .find(Boolean);
  if (!line) return '';
  return line
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~#>]+/g, '')
    .trim();
}

function toThreadMessage(comment: ThreadComment, email: ChannelEmail | null): EmailThreadMessage {
  const authorName = comment.author ? getAssigneeDisplayDetails(comment.author).primary : null;
  return {
    attachments: email?.attachments ?? [],
    ccAddresses: email?.ccAddresses ?? [],
    commentId: comment.id,
    createdAt: comment.createdAt,
    fromAddress: email?.fromAddress ?? null,
    isAiAuthored: comment.isAiAuthored,
    senderName: comment.isAiAuthored
      ? ALINE_AI_NAME
      : (email?.fromName ??
        authorName ??
        comment.importedAuthorName ??
        email?.fromAddress ??
        'Someone'),
    text: comment.text.trim(),
    toAddresses: email?.toAddresses ?? [],
  };
}
